/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */
/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedNode').BalancedNode} BalancedNode */
/** @typedef {import('../../generic/utilities/objectStructures/balanced/DeviceGroupDevice').DeviceGroupDevice} DeviceGroupDevice */

import { Fragment, useEffect, useState } from 'react'
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import BalancedColumnReorderDialog from './BalancedColumnReorderDialog'
import SmartSearch from '../../generic/formComponent/Search'
import { canInsertBefore, canPlaceInColumn, canReorderOnto, reorderBeforeId } from '../../generic/utilities/objectStructures/balanced/balancedConstraints'
import { matchesCodeOrName } from '../../generic/utilities/objectStructures/balanced/balancedSearch'
import { formatBalancedLabel } from '../../generic/utilities/objectStructures/balanced/balancedLabel'
import { COMPACT_SEARCH_SX, SEARCH_MATCH_BACKGROUND, SEARCH_MATCH_OUTLINE, MULTI_SELECT_BACKGROUND, MULTI_SELECT_OUTLINE, HAS_CHANGES_BORDER } from '../../generic/utilities/objectStructures/balanced/balancedStyles'

const COLUMN_WIDTH = 220
const MIN_COLUMN_WIDTH = 120
const COLLAPSED_COLUMN_WIDTH = 40

const CARD_BACKGROUND = 'rgba(255,255,255,0.05)'

/**
 * @param {Object} props
 * @param {BalancedData}         props.balancedData        - Shared balanced state
 * @param {DeviceGroupDevice[]}  props.deviceGroupDevices  - Devices loaded when balancedType is 'LINE'
 * @param {Object}               props.columnNodeIds       - Map of columnKey → nodeId[] (owned by BalancedForm)
 * @param {BalancedNode|null}    props.draggingNode        - Node currently being dragged (for placement feedback)
 * @param {boolean}              [props.disabled]          - When true, all drag interactions are disabled
 * @param {Function}             [props.columnPositionAllows] - (columnKey, node) => boolean line-advancement range check
 * @param {Function}             props.onColumnDrop        - Called with (node, targetColumnKey, insertBeforeId?)
 * @param {Function}             props.onNodesReordered    - Called with (columnKey, newOrderedIds) after reorder
 * @param {Function}             props.onNodeDragStart     - Called with the dragged node when a card drag begins
 * @param {Function}             props.onNodeDragEnd       - Called when a card drag ends
 */
const BalancedFormColumns = ({ balancedData, deviceGroupDevices, columnNodeIds, draggingNode, disabled = false, columnPositionAllows = () => true, onColumnDrop, onNodesReordered, onNodeDragStart, onNodeDragEnd, onNodeSelected = () => {}, selectedNodeId = null, multiSelection = new Set(), onToggleMultiSelect = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [draggingOver, setDraggingOver] = useState(null)
  const [dropTarget, setDropTarget] = useState(null)
  const [reorderDialog, setReorderDialog] = useState({ open: false, sourceNodeId: null, columnKey: null })
  // Per-column search term (columnKey → term); highlights matches in that column only.
  const [columnSearch, setColumnSearch] = useState({})
  // Column keys collapsed to a thin strip to free horizontal space.
  const [collapsedColumns, setCollapsedColumns] = useState(new Set())

  /**
   * Collapses/expands a column. A collapsed column shrinks to a strip; expanding
   * reopens it at the minimum width.
   *
   * @param {string} key - Column key
   */
  const toggleColumnCollapsed = (key) => {
    setCollapsedColumns(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Per-column width in px (independent of the others). Collapsed columns render at
  // the fixed strip width regardless of their stored width.
  const [columnWidths, setColumnWidths] = useState({})

  /**
   * Current rendered width of a column.
   *
   * @param {string} key - Column key
   * @returns {number}
   */
  const widthOf = (key) => collapsedColumns.has(key) ? COLLAPSED_COLUMN_WIDTH : (columnWidths[key] ?? COLUMN_WIDTH)

  /**
   * Starts a drag on a column's right-edge handle. Resizing only changes that
   * column's width (its neighbours are untouched); the row grows and scrolls.
   *
   * @param {React.MouseEvent} e
   * @param {string} key - Column key being resized
   */
  const startColumnResize = (e, key) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = widthOf(key)
    const onMove = (ev) => {
      const next = Math.max(MIN_COLUMN_WIDTH, startWidth + (ev.clientX - startX))
      setColumnWidths(prev => ({ ...prev, [key]: next }))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // Clear visual state in the same render batch as the column update (avoids flicker)
  useEffect(() => {
    if (!draggingNode) {
      setDraggingOver(null)
      setDropTarget(null)
    }
  }, [draggingNode])

  const { balancedType, selectedType } = balancedData

  const nodeMap = Object.fromEntries((balancedData.nodes ?? []).map(n => [n.id, n]))

  /**
   * Returns the ordered node objects for a column by resolving its IDs against
   * `nodeMap`, dropping any IDs that no longer exist in the node list.
   *
   * @param {string} key - Column key
   * @returns {BalancedNode[]}
   */
  const getOrderedNodes = (key) => (columnNodeIds[key] ?? []).map(id => nodeMap[id]).filter(Boolean)

  /**
   * Returns true when the node currently being dragged originated from `key`.
   * @param {string} key - Column key to test
   * @returns {boolean}
   */
  const isSameColumn = (key) => draggingNode?._sourceColumnKey === key

  const deviceByKey = Object.fromEntries((deviceGroupDevices ?? []).map(d => [String(d.id), d]))

  /**
   * Determines whether a column accepts a node based on technology compatibility.
   *
   * In LINE mode, the column maps to a device; the node is accepted only if its
   * job technology is among the device's supported technologies. Always returns
   * true in TYPE mode.
   *
   * @param {string} key - Column key (device id in LINE mode, position in TYPE mode)
   * @param {BalancedNode} node - The node to test
   * @returns {boolean}
   */
  const columnAcceptsNode = (key, node) => {
    if (balancedType !== 'LINE') return true
    const device = deviceByKey[key]
    if (!device) return false
    const techId = node?.jobReference?.technology?.id
    return (device.technologies ?? []).some(t => t.id === techId)
  }

  /**
   * Combined drag-feedback predicate: a column accepts the dragged node only when
   * both the technology (LINE) and the line-advancement position range allow it.
   * Used for visual feedback only; the explanatory alert is raised on drop by the
   * parent `BalancedForm`.
   *
   * @param {string} key - Column key
   * @param {BalancedNode} node - The node to test
   * @returns {boolean}
   */
  const columnAllowsDrag = (key, node) => columnAcceptsNode(key, node) && columnPositionAllows(key, node)

  /**
   * Returns true when a card should be visually dimmed during a drag operation.
   *
   * A card is dimmed when it is not the dragged node and either the column does
   * not accept the dragged node's technology, or the card is not a valid drop
   * target (neither a reorder candidate within the same column nor a valid
   * insert-before position for a cross-column drop).
   *
   * @param {BalancedNode} card - The card being evaluated
   * @param {BalancedNode[]} columnNodes - All nodes currently in this column
   * @param {string} key - Column key
   * @param {boolean} sameColumn - Whether the dragged node originates from this column
   * @returns {boolean}
   */
  const isCardDimmed = (card, columnNodes, key, sameColumn) => {
    if (!draggingNode || draggingNode._isGroup) return false
    if (card.id === draggingNode.id) return false
    if (!columnAllowsDrag(key, draggingNode)) return true
    return sameColumn
      ? !canReorderOnto(draggingNode, columnNodes, card.id)
      : !canInsertBefore(draggingNode, columnNodes, card.id)
  }

  const SLOT_HEIGHT = '1.7rem'

  /**
   * Returns the inline style for the dashed insertion-slot indicator shown
   * above (`'top'`) or below (`'bottom'`) a card during drag-over.
   *
   * @param {'top'|'bottom'} side - Which edge of the card to attach the slot to
   * @returns {React.CSSProperties}
   */
  const slotStyle = (side) => ({
    position: 'absolute',
    left: '2px',
    right: '2px',
    [side]: '2px',
    height: '1.4rem',
    border: '2px dashed ' + theme.palette.primary.main,
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.04)',
    pointerEvents: 'none'
  })

  const emptySlotStyle = {
    height: '1.4rem',
    flexShrink: 0,
    border: '2px dashed ' + theme.palette.primary.main,
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.04)',
    pointerEvents: 'none'
  }

  /**
   * Derives the list of column descriptors from `balancedType` and `selectedType`.
   *
   * - `LINE`: one column per device in `deviceGroupDevices`, keyed by device id,
   *   ordered ascending by the device `positionInLine` (physical line position).
   * - `TYPE`: one column per position (1..selectedType), keyed by position number.
   *
   * @returns {{ key: string, label: string }[]}
   */
  const buildColumns = () => {
    if (balancedType === 'LINE') {
      return [...deviceGroupDevices]
        .sort((a, b) => (a.positionInLine ?? 0) - (b.positionInLine ?? 0))
        .map((device) => ({ key: String(device.id), label: device.name }))
    }
    if (balancedType === 'TYPE' && selectedType) {
      const count = parseInt(selectedType, 10)
      if (!isNaN(count) && count > 0) {
        return Array.from({ length: count }, (_, i) => ({
          key: String(i + 1),
          label: t_('Position') + ' ' + (i + 1)
        }))
      }
    }
    return []
  }

  const columns = buildColumns()

  if (columns.length === 0) return null

  /**
   * Applies the reorder confirmed in the dialog and closes it.
   * @param {number[]} newOrderedIds - Node IDs in the confirmed new order
   */
  const handleReorderAccept = (newOrderedIds) => {
    onNodesReordered?.(reorderDialog.columnKey, newOrderedIds)
    setReorderDialog({ open: false, sourceNodeId: null, columnKey: null })
  }

  /**
   * Resolves the node ID that the dragged node should be inserted before.
   *
   * For same-column reorders, delegates to `reorderBeforeId` for a stable swap.
   * For cross-column drops, uses cursor Y position relative to the card:
   * top half → insert before the card; bottom half → insert after it (`null` if last).
   *
   * @param {DragEvent} e - The drag event carrying cursor position
   * @param {string} key - Column key
   * @param {number} cardNodeId - ID of the card being hovered
   * @returns {number|null} Target insert-before ID, or null to append at end
   */
  const getInsertBeforeId = (e, key, cardNodeId) => {
    const ordered = getOrderedNodes(key)
    if (isSameColumn(key) && draggingNode) {
      return reorderBeforeId(ordered, draggingNode.id, cardNodeId)
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const isBottomHalf = e.clientY > rect.top + rect.height / 2
    if (!isBottomHalf) return cardNodeId
    const cardIndex = ordered.findIndex(n => n.id === cardNodeId)
    const next = cardIndex >= 0 && cardIndex < ordered.length - 1 ? ordered[cardIndex + 1] : null
    return next?.id ?? null
  }

  /**
   * Validates the drag over a column's background drop zone and updates feedback state.
   *
   * The drop EVENT is allowed whenever the technology is compatible, so an
   * out-of-range position still reaches `BalancedForm`, which explains it with an
   * alert and refuses the move. The active highlight + insertion slot are shown
   * only when the drop would actually succeed (position and ordering valid).
   *
   * @param {DragEvent} e
   * @param {string} key - Column key being hovered
   */
  const handleColumnDragOver = (e, key) => {
    e.preventDefault()
    const techOk = !draggingNode || draggingNode._isGroup || columnAcceptsNode(key, draggingNode)
    if (!techOk) {
      e.dataTransfer.dropEffect = 'none'
      setDraggingOver(prev => prev === key ? null : prev)
      setDropTarget(prev => prev?.key === key ? null : prev)
      return
    }
    e.dataTransfer.dropEffect = 'move'
    const valid = draggingNode?._isGroup || (columnPositionAllows(key, draggingNode) && canInsertBefore(draggingNode, getOrderedNodes(key), null))
    if (!valid) {
      setDraggingOver(prev => prev === key ? null : prev)
      setDropTarget(prev => prev?.key === key ? null : prev)
      return
    }
    setDraggingOver(key)
    setDropTarget({ key, beforeId: null })
  }

  /**
   * Clears the drag-over highlight for a column when the cursor leaves its bounds.
   * Uses `relatedTarget` to avoid false positives caused by entering a child element.
   *
   * @param {DragEvent} e
   * @param {string} key - Column key the cursor is leaving
   */
  const handleColumnDragLeave = (e, key) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggingOver(prev => prev === key ? null : prev)
      setDropTarget(prev => prev?.key === key ? null : prev)
    }
  }

  /**
   * Handles a drop on the column's background drop zone (not on a specific card).
   *
   * Parses the drag payload and delegates to `onColumnDrop` to append the node
   * at the end of the column, provided all placement constraints pass.
   *
   * @param {DragEvent} e
   * @param {string} key - Target column key
   */
  const handleColumnDrop = (e, key) => {
    e.preventDefault()
    try {
      const node = JSON.parse(e.dataTransfer.getData('application/json'))
      if (node._isGroup) { onColumnDrop?.(node, key, null); return }
      if (!columnAcceptsNode(key, node)) return
      // Position and ordering are validated (and alerted) by BalancedForm.
      onColumnDrop?.(node, key, null)
    } catch {
      // ignore malformed drag data
    }
  }

  /**
   * Validates a drag over a specific card and updates the precise insertion target.
   *
   * Stops propagation so the column's own `onDragOver` does not also fire.
   * Updates `dropTarget` with the exact `beforeId` computed from cursor position
   * or reorder logic; clears it if the drop position is invalid.
   *
   * @param {DragEvent} e
   * @param {string} key - Column key containing the card
   * @param {number} cardNodeId - ID of the card being hovered
   */
  const handleNodeDragOver = (e, key, cardNodeId) => {
    e.preventDefault()
    e.stopPropagation()
    const ordered = getOrderedNodes(key)
    const sameColumn = isSameColumn(key)
    const insertBeforeId = getInsertBeforeId(e, key, cardNodeId)
    const techOk = !draggingNode || draggingNode._isGroup || columnAcceptsNode(key, draggingNode)
    if (!techOk) {
      e.dataTransfer.dropEffect = 'none'
      setDropTarget(null)
      return
    }
    e.dataTransfer.dropEffect = 'move'
    const valid = draggingNode?._isGroup || (sameColumn
      ? canReorderOnto(draggingNode, ordered, cardNodeId)
      : columnPositionAllows(key, draggingNode) && canInsertBefore(draggingNode, ordered, insertBeforeId))
    if (!valid) {
      setDropTarget(null)
      return
    }
    setDraggingOver(key)
    setDropTarget({ key, beforeId: sameColumn ? reorderBeforeId(ordered, draggingNode.id, cardNodeId) : insertBeforeId })
  }

  /**
   * Handles a drop on a specific card.
   *
   * Stops propagation so the column's background handler does not also fire.
   * For same-column reorders, delegates to `onColumnDrop` with the reorder target;
   * for cross-column moves, resolves the insertion position from cursor Y and
   * delegates accordingly.
   *
   * @param {DragEvent} e
   * @param {string} targetKey - Column key containing the target card
   * @param {number} cardNodeId - ID of the card being dropped onto
   */
  const handleNodeDrop = (e, targetKey, cardNodeId) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const node = JSON.parse(e.dataTransfer.getData('application/json'))
      if (node._isGroup) { onColumnDrop?.(node, targetKey, null); return }
      const ordered = getOrderedNodes(targetKey)
      if (!columnAcceptsNode(targetKey, node)) return
      if (node._sourceColumnKey === targetKey) {
        if (!canReorderOnto(node, ordered, cardNodeId)) return
        onColumnDrop?.(node, targetKey, reorderBeforeId(ordered, node.id, cardNodeId))
      } else {
        // Cross-column: position and ordering are validated (and alerted) by BalancedForm.
        const insertBeforeId = getInsertBeforeId(e, targetKey, cardNodeId)
        onColumnDrop?.(node, targetKey, insertBeforeId)
      }
    } catch {
      // ignore malformed drag data
    }
  }

  return (
    <>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowX: 'auto', overflowY: 'hidden', padding: '0 0.75rem 0.75rem' }}>
        <div style={{ display: 'flex', height: '100%', width: 'max-content' }}>
          {columns.map(({ key, label }, index) => {
            const nodes = (columnNodeIds[key] ?? []).map(id => nodeMap[id]).filter(Boolean)
            const isOver = draggingOver === key
            const sameColumn = isSameColumn(key)
            const showDropHere = !!draggingNode && dropTarget?.key === key
            const columnPlaceable = !draggingNode || draggingNode._isGroup || (columnAllowsDrag(key, draggingNode) && (sameColumn
              ? nodes.some(n => canReorderOnto(draggingNode, nodes, n.id))
              : canPlaceInColumn(draggingNode, nodes)))
            const isCollapsed = collapsedColumns.has(key)
            return (
              <Fragment key={key}>
                <div
                  style={{ width: `${widthOf(key)}px`, flexShrink: 0, height: '100%', position: 'relative' }}
                >
                  <div
                    data-collapsed={isCollapsed || undefined}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}
                  >
                    {isCollapsed
                      ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', paddingTop: '0.25rem' }}>
                          <Tooltip title={label} placement='right'>
                            <IconButton size='small' aria-label={t_('Expand') + ' ' + label} onClick={() => toggleColumnCollapsed(key)}>
                              <KeyboardArrowRightRounded fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Typography variant='caption' color='primary' noWrap style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', marginTop: '0.5rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', overflow: 'hidden', maxHeight: '100%' }}>
                            {label}
                          </Typography>
                        </div>
                        )
                      : (
                        <>
                          <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.25rem' }}>
                              <Typography variant='caption' color='primary' noWrap style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {label}
                              </Typography>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                                <Typography variant='caption' style={{ color: 'rgba(255,255,255,0.4)' }}>
                                  {nodes.length} {t_('ops')}
                                </Typography>
                                <Tooltip title={t_('Collapse')} placement='top'>
                                  <IconButton size='small' aria-label={t_('Collapse') + ' ' + label} onClick={() => toggleColumnCollapsed(key)}>
                                    <KeyboardArrowLeftRounded fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                            <SmartSearch
                              setFilterText={(value) => setColumnSearch(prev => ({ ...prev, [key]: value }))}
                              loadTable={false}
                              setLoadTable={() => {}}
                              sx={{ width: '100%', marginTop: '0.25rem', ...COMPACT_SEARCH_SX }}
                            />
                          </div>
                          <div
                            onDragOver={disabled ? undefined : (e) => handleColumnDragOver(e, key)}
                            onDragLeave={disabled ? undefined : (e) => handleColumnDragLeave(e, key)}
                            onDrop={disabled ? undefined : (e) => handleColumnDrop(e, key)}
                            style={{
                              flexGrow: 1,
                              minHeight: 0,
                              overflowY: 'auto',
                              padding: '0.5rem',
                              border: (isOver && columnPlaceable) ? '2px dashed rgba(255,255,255,0.4)' : '2px dashed transparent',
                              borderRadius: '2px',
                              opacity: columnPlaceable ? 1 : 0.4,
                              transition: 'border-color 0.15s, opacity 0.15s',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                            }}
                          >
                            {nodes.length === 0 && !showDropHere && (
                              <Typography variant='caption' style={{ color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginTop: '1rem' }}>
                                {t_('Empty')}
                              </Typography>
                            )}
                            {nodes.map((node, nodeIndex) => {
                              const dropBefore = showDropHere && dropTarget.beforeId === node.id
                              const dropAfterLast = showDropHere && dropTarget.beforeId === null && nodeIndex === nodes.length - 1
                              const searchMatch = matchesCodeOrName(node.code, node.name, columnSearch[key])
                              const isSelected = selectedNodeId != null && node.id === selectedNodeId
                              const multiSelected = multiSelection.has(node.id)
                              const hasChanges = node.hasChanges === true
                              return (
                                <div
                                  key={node.id}
                                  data-search-match={searchMatch || undefined}
                                  data-selected={isSelected || undefined}
                                  data-multi-selected={multiSelected || undefined}
                                  data-has-changes={hasChanges || undefined}
                                  onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey) {
                                      e.stopPropagation()
                                      onToggleMultiSelect(node)
                                    } else {
                                      onNodeSelected(node)
                                    }
                                  }}
                                  draggable={!disabled}
                                  onDragStart={disabled
                                    ? undefined
                                    : (e) => {
                                      // Drag the whole Ctrl/Cmd+click group when this card is part of it (S12).
                                        const payload = multiSelection.has(node.id)
                                          ? { _isGroup: true, _sourceColumnKey: key, _nodes: (balancedData.nodes ?? []).filter(n => n.nodeType === 'JOB' && multiSelection.has(n.id)) }
                                          : { ...node, _sourceColumnKey: key }
                                        e.dataTransfer.setData('application/json', JSON.stringify(payload))
                                        e.dataTransfer.effectAllowed = 'move'
                                        onNodeDragStart?.(payload)
                                      }}
                                  onDragEnd={disabled ? undefined : () => onNodeDragEnd?.()}
                                  onDragOver={disabled ? undefined : (e) => handleNodeDragOver(e, key, node.id)}
                                  onDrop={disabled ? undefined : (e) => handleNodeDrop(e, key, node.id)}
                                  onDoubleClick={disabled ? undefined : () => setReorderDialog({ open: true, sourceNodeId: node.id, columnKey: key })}
                                  style={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '0.4rem',
                                    paddingLeft: '6px',
                                    paddingRight: '6px',
                                    paddingTop: dropBefore ? SLOT_HEIGHT : '4px',
                                    paddingBottom: dropAfterLast ? SLOT_HEIGHT : '4px',
                                    borderRadius: '3px',
                                    background: multiSelected ? MULTI_SELECT_BACKGROUND : searchMatch ? SEARCH_MATCH_BACKGROUND : CARD_BACKGROUND,
                                    outline: multiSelected ? MULTI_SELECT_OUTLINE : searchMatch ? SEARCH_MATCH_OUTLINE : 'none',
                                    borderLeft: hasChanges ? HAS_CHANGES_BORDER : 'none',
                                    boxShadow: isSelected ? 'inset 0 0 0 2px ' + theme.palette.primary.main : 'none',
                                    overflow: 'hidden',
                                    cursor: 'grab',
                                    opacity: isCardDimmed(node, nodes, key, sameColumn) ? 0.35 : 1,
                                    transition: 'opacity 0.15s, padding 0.1s'
                                  }}
                                >
                                  {dropBefore && <div style={slotStyle('top')} />}
                                  <Typography variant='caption' color='primary' style={{ fontWeight: 600, flexShrink: 0 }}>
                                    {String(node.jobReference?.jobOrder ?? '').padStart(3, '0')}
                                  </Typography>
                                  <Tooltip title={formatBalancedLabel(node.code, node.name)} placement='top' enterDelay={500}>
                                    <Typography variant='body2' noWrap style={{ flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {formatBalancedLabel(node.code, node.name)}
                                    </Typography>
                                  </Tooltip>
                                  {node.jobReference?.duration && (
                                    <Typography variant='caption' style={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)' }}>
                                      {node.jobReference.duration}
                                    </Typography>
                                  )}
                                  {dropAfterLast && <div style={slotStyle('bottom')} />}
                                </div>
                              )
                            })}
                            {nodes.length === 0 && showDropHere && <div style={emptySlotStyle} />}
                          </div>
                        </>
                        )}
                  </div>
                </div>
                {!isCollapsed && (
                  <Box
                    onMouseDown={(e) => startColumnResize(e, key)}
                    title={t_('Resize')}
                    sx={{
                      width: '1rem',
                      flexShrink: 0,
                      cursor: 'col-resize',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '&:hover .col-resize-bar': { backgroundColor: 'primary.main', height: '4.5rem', width: '4px' }
                    }}
                  >
                    <Box
                      className='col-resize-bar'
                      sx={{ width: '3px', height: '3rem', backgroundColor: 'divider', borderRadius: '3px', transition: 'all 0.2s ease-in-out', pointerEvents: 'none' }}
                    />
                  </Box>
                )}
              </Fragment>
            )
          })}
        </div>
      </div>
      <BalancedColumnReorderDialog
        open={reorderDialog.open}
        onClose={() => setReorderDialog({ open: false, sourceNodeId: null, columnKey: null })}
        onAccept={handleReorderAccept}
        sourceNode={reorderDialog.sourceNodeId ? nodeMap[reorderDialog.sourceNodeId] : null}
        columnNodes={reorderDialog.columnKey ? (columnNodeIds[reorderDialog.columnKey] ?? []).map(id => nodeMap[id]).filter(Boolean) : []}
      />
    </>
  )
}

export default BalancedFormColumns
